/**
 * Read auth tokens from native Capacitor cookie jar (httpOnly cookies
 * are not visible via document.cookie on iOS).
 */

import { Capacitor, CapacitorCookies } from '@capacitor/core';
import { AUTH_COOKIE_NAMES, getAuthCookies } from '@/utils/cookies';

const COOKIE_URL_CANDIDATES = [
  import.meta.env.VITE_AUTH_API_BASE?.trim(),
  import.meta.env.VITE_API_PATH?.trim(),
  'https://artist-api.starclinch.com',
].filter(Boolean) as string[];

function cookieValueFromMap(
  cookies: Record<string, string> | undefined,
  key: string,
): string | null {
  if (!cookies) return null;
  const value = cookies[key]?.trim();
  return value || null;
}

export async function getNativeAuthTokensFromCookies(): Promise<{
  access: string | null;
  refresh: string | null;
  cookieUrl: string | null;
  cookieKeys: string[];
}> {
  if (!Capacitor.isNativePlatform()) {
    const auth = getAuthCookies();
    const access = auth.access?.trim() || null;
    const refresh = auth.refresh?.trim() || null;
    const cookieKeys = [access ? 'access' : null, refresh ? 'refresh' : null].filter(
      Boolean,
    ) as string[];
    return { access, refresh, cookieUrl: null, cookieKeys };
  }

  const seenOrigins = new Set<string>();
  for (const candidate of COOKIE_URL_CANDIDATES) {
    try {
      const url = new URL(candidate, 'https://artist-api.starclinch.com').origin;
      if (seenOrigins.has(url)) continue;
      seenOrigins.add(url);

      const cookies = (await CapacitorCookies.getCookies({ url })) as Record<string, string>;
      const access =
        cookieValueFromMap(cookies, AUTH_COOKIE_NAMES.ACCESS) ||
        cookieValueFromMap(cookies, 'access');
      const refresh =
        cookieValueFromMap(cookies, AUTH_COOKIE_NAMES.REFRESH) ||
        cookieValueFromMap(cookies, 'refresh');
      if (access || refresh) {
        return {
          access,
          refresh,
          cookieUrl: url,
          cookieKeys: Object.keys(cookies),
        };
      }
    } catch {
      // try next candidate URL
    }
  }

  return { access: null, refresh: null, cookieUrl: null, cookieKeys: [] };
}

export async function getNativeAccessTokenFromCookies(): Promise<{
  token: string | null;
  cookieUrl: string | null;
  cookieKeys: string[];
}> {
  const { access, cookieUrl, cookieKeys } = await getNativeAuthTokensFromCookies();
  return { token: access, cookieUrl, cookieKeys };
}

export async function getNativeRefreshTokenFromCookies(): Promise<string | null> {
  const { refresh } = await getNativeAuthTokensFromCookies();
  return refresh;
}
